import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useTranslation } from "@/i18n/translations";

const FIELDS = ["business_legal_name", "business_address", "business_email", "business_privacy_email", "business_phone", "privacy_policy_url",
  "marketing_enabled", "marketing_existing_customers", "health_data_enabled", "chat_retention_days", "booking_retention_policy"];

// Settings → "Privacy & legal": the salon is the controller of its clients' data; Chatity is the processor.
export default function PrivacySettings({ API, salon, onSaved }) {
  const { t } = useTranslation();
  const tf = (k, fb) => { const v = t(k); return v && v !== k ? v : fb; };
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!salon) return;
    setForm(Object.fromEntries(FIELDS.map((k) => [k, salon[k] ?? (k === "chat_retention_days" ? 90 : k === "booking_retention_policy" ? "active_relationship" : (k.endsWith("_enabled") || k === "marketing_existing_customers") ? false : "")])));
  }, [salon]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, chat_retention_days: Math.max(7, Math.min(730, parseInt(form.chat_retention_days, 10) || 90)) };
      const r = await axios.put(`${API}/salons/${salon.id}`, payload);
      onSaved?.(r.data);
      toast.success(tf("privacy.saved", "Настройки конфиденциальности сохранены"));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const text = (k, label, placeholder, type = "text") => (
    <div>
      <Label className="text-sm font-medium">{label}</Label>
      <Input type={type} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={placeholder}
        className="mt-1 border-2 border-gray-300" data-testid={`privacy-${k.replace(/_/g, "-")}`} />
    </div>
  );
  const check = (k, label, hint) => (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} className="w-5 h-5 mt-0.5 border-2 border-black cursor-pointer"
        data-testid={`privacy-${k.replace(/_/g, "-")}`} />
      <span><span className="font-medium">{label}</span>{hint && <span className="block text-xs text-gray-500">{hint}</span>}</span>
    </label>
  );

  return (
    <div className="space-y-4" data-testid="privacy-settings">
      <h3 className="text-xl font-semibold border-b-2 border-black pb-2">🔒 {tf("privacy.title", "Конфиденциальность и юридические данные")}</h3>
      <p className="text-sm text-gray-600">
        {tf("privacy.intro", "По GDPR вы — контролёр данных ваших клиентов, Chatity (MINDUNITE SAS) — обработчик. Эти данные показываются клиентам в уведомлении о конфиденциальности на мини-сайте и в чат-виджете.")}
        {" "}<a href="/dpa" target="_blank" rel="noreferrer" className="underline">DPA</a> · <a href="/subprocessors" target="_blank" rel="noreferrer" className="underline">Subprocessors</a>
      </p>

      <div className="grid md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg border-2 border-gray-300">
        {text("business_legal_name", tf("privacy.legalName", "Юридическое название"), "Marina Rubio EI / SAS …")}
        {text("business_address", tf("privacy.address", "Юридический адрес"), "7 Avenue …, 06200 Nice")}
        {text("business_email", tf("privacy.email", "Контактный email"), "contact@…", "email")}
        {text("business_privacy_email", tf("privacy.privacyEmail", "Email для запросов о данных"), "privacy@…", "email")}
        {text("business_phone", tf("privacy.phone", "Телефон"), "+33 …", "tel")}
        {text("privacy_policy_url", tf("privacy.policyUrl", "Ссылка на вашу политику конфиденциальности (необязательно)"), "https://…", "url")}
      </div>

      <div className="space-y-3 bg-gray-50 p-4 rounded-lg border-2 border-gray-300">
        <h4 className="font-semibold">{tf("privacy.marketingTitle", "Маркетинговые рассылки")}</h4>
        {check("marketing_enabled", tf("privacy.marketingEnabled", "Разрешить маркетинговые сообщения клиентам (реактивация, акции)"),
          tf("privacy.marketingHint", "На форме записи появится отдельный невыбранный чекбокс согласия. Без согласия рекламные сообщения не отправляются; напоминания о записи работают всегда."))}
        {form.marketing_enabled && check("marketing_existing_customers", tf("privacy.existingCustomers", "Исключение для существующих клиентов"),
          tf("privacy.existingCustomersHint", "Отправлять предложения по похожим услугам клиентам, которые уже были у вас, если они не отписались (STOP). Убедитесь, что это допустимо по вашему законодательству."))}
      </div>

      <div className="space-y-3 bg-gray-50 p-4 rounded-lg border-2 border-gray-300">
        <h4 className="font-semibold">{tf("privacy.retentionTitle", "Сроки хранения")}</h4>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium">{tf("privacy.chatRetention", "Переписка с клиентами, дней")}</Label>
            <Input type="number" min={7} max={730} value={form.chat_retention_days ?? 90} onChange={(e) => set("chat_retention_days", e.target.value)}
              className="mt-1 border-2 border-gray-300" data-testid="privacy-chat-retention-days" />
          </div>
          <div>
            <Label className="text-sm font-medium">{tf("privacy.bookingRetention", "История записей и карточки клиентов")}</Label>
            <select value={form.booking_retention_policy || "active_relationship"} onChange={(e) => set("booking_retention_policy", e.target.value)}
              className="mt-1 w-full border-2 border-gray-300 rounded-md px-3 py-2 text-sm bg-white" data-testid="privacy-booking-retention-policy">
              <option value="active_relationship">{tf("privacy.retention.active", "Пока клиент активен (3 года после последнего визита)")}</option>
              <option value="2y">{tf("privacy.retention.2y", "2 года после последнего визита")}</option>
              <option value="1y">{tf("privacy.retention.1y", "1 год после последнего визита")}</option>
            </select>
          </div>
        </div>
        <p className="text-xs text-gray-500">{tf("privacy.retentionHint", "Автоматическое удаление/анонимизация по этим срокам выполняется ежедневной задачей.")}</p>
      </div>

      <div className="space-y-3 bg-gray-50 p-4 rounded-lg border-2 border-gray-300">
        <h4 className="font-semibold">{tf("privacy.healthTitle", "Медицинские данные")}</h4>
        {check("health_data_enabled", tf("privacy.healthEnabled", "Режим работы с данными о здоровье (PMU, эстетическая медицина)"),
          tf("privacy.healthHint", "По умолчанию ИИ не запрашивает и не сохраняет медицинскую информацию и передаёт такие вопросы администратору. Отдельный защищённый процесс для медицинских данных — в разработке."))}
      </div>

      <Button onClick={save} disabled={saving} className="bg-black text-white hover:bg-gray-800 border-2 border-black" data-testid="privacy-save-button">
        {saving ? "…" : tf("privacy.save", "Сохранить настройки конфиденциальности")}
      </Button>
    </div>
  );
}
